/* eslint-disable no-restricted-syntax */
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  EMPTY,
  finalize,
  map,
  Observable,
  Subject,
  take,
  takeUntil,
  tap,
} from 'rxjs';

import { OrderService } from 'src/app/core/services/order';
import { TicketService } from 'src/app/core/services/ticket';
import { ContentDocumentLink } from 'src/app/models/content-document-link.model';
import { Order } from 'src/app/models/order.model';
import { Service } from 'src/app/models/service.model';
import { Ticket } from 'src/app/models/ticket/ticket.model';
import { User } from 'src/app/models/user.model';
import { Permission } from 'src/app/shared/constants/permissions';
import * as fromStore from 'src/app/store/';
import { environment } from 'src/environments/environment';

export enum SupportTypeLabel {
  SECURITY = 'Security',
  SERVICE = 'Service',
  BILLING = 'Billing',
  TOB = 'TOB',
  CARE = 'Care',
  DISCONNECT = 'Disconnect',
}

@Component({
  selector: 'app-create-ticket',
  templateUrl: './create-ticket.component.html',
  styleUrls: ['./create-ticket.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CreateTicketComponent
  implements OnInit, AfterContentInit, OnDestroy
{
  browserSize: string;

  dialogWidth: string;

  dialogHeight: string;

  private fb = inject(UntypedFormBuilder);

  ticketFormGroup: UntypedFormGroup = this.fb.group({
    supportType: this.fb.control('', Validators.required),
  });

  formSubmitAttempted: boolean = false;

  inlineErrors: any = {};

  createdOrder: Order = null;

  errorMessage: string = null;

  Permission = Permission;

  private hasSubmissionErrorSubject: BehaviorSubject<boolean> =
    new BehaviorSubject(false);

  hasSubmissionError$ = this.hasSubmissionErrorSubject.asObservable();

  private isTicketOptionSelectedSubject = new BehaviorSubject<boolean>(false);

  isTicketOptionSelected$ = this.isTicketOptionSelectedSubject.asObservable();

  private ticketSubmittedSubject = new BehaviorSubject<boolean>(false);

  ticketSubmitted$ = this.ticketSubmittedSubject.asObservable();

  private isSubmittingSubject: BehaviorSubject<boolean> = new BehaviorSubject(
    false,
  );

  private store = inject(Store<fromStore.State>);

  isSubmitting$: Observable<boolean> = this.isSubmittingSubject.asObservable();

  invoices$: Observable<ContentDocumentLink[]> = this.store.select(
    fromStore.getAllInvoiceAttachments,
  );

  areInvoicesLoading$: Observable<boolean> = this.store.select(
    fromStore.getInvoiceAttachmentsLoading,
  );

  areInvoicesLoaded$: Observable<boolean> = this.store.select(
    fromStore.getInvoiceAttachmentsLoaded,
  );

  services$: Observable<Service[]> = this.store.select(
    fromStore.getServicesInService,
  );

  areServicesLoading$: Observable<boolean> = this.store.select(
    fromStore.getServicesLoading,
  );

  areServicesLoaded$: Observable<boolean> = this.store.select(
    fromStore.getServicesLoaded,
  );

  tickets$: Observable<Ticket[]> = this.store.select(fromStore.getAllTickets);

  areTicketsLoading$: Observable<boolean> = this.store.select(
    fromStore.getTicketsLoading,
  );

  areTicketsLoaded$: Observable<boolean> = this.store.select(
    fromStore.getTicketsLoaded,
  );

  selectedServiceId: string;

  contacts$: Observable<User[]> = this.store.select(fromStore.getContacts);

  private destroy$ = new Subject<void>();

  hideCustomerTicketNumber$: Observable<boolean> = this.store
    .select(fromStore.getCurrentUser)
    .pipe(
      map((user) => user?.Account?.Can_Set_Custom_Ticket_Number__c === false),
      distinctUntilChanged(),
    );

  disconnectReasons$: Observable<string[]> = this.store.select(
    fromStore.getDisconnectReasons,
  );

  supportTypeLabel = SupportTypeLabel;

  dialogRef = inject(MatDialogRef<CreateTicketComponent>);

  private readonly dialogData = inject(MAT_DIALOG_DATA);

  private ticketService = inject(TicketService);

  private orderService = inject(OrderService);

  private cdr = inject(ChangeDetectorRef);

  get canSubmit(): boolean {
    return true;
  }

  ngOnInit(): void {
    this.dialogRef.disableClose = true;
    this.browserSize = this.dialogData.browserSize;
    this.dialogWidth =
      this.browserSize === 'small' ? 'calc(100vw - 20px)' : '650px';
    this.dialogHeight = this.browserSize === 'small' ? '315px' : '380px';
    this.cdr.markForCheck();

    // ✅ Dispatch only if services or invoices are missing
    combineLatest([this.areServicesLoaded$, this.areInvoicesLoaded$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([areServicesLoaded, areInvoicesLoaded]) => {
        if (!areServicesLoaded) {
          this.store.dispatch(new fromStore.LoadServices());
        }
        if (!areInvoicesLoaded) {
          this.store.dispatch(new fromStore.LoadInvoiceAttachments());
        }
      });

    this.store
      .select(fromStore.getContactsLoaded)
      .pipe(take(1))
      .subscribe((loaded) => {
        if (!loaded) {
          this.store.dispatch(new fromStore.LoadContacts());
        }
      });

    this.store.dispatch(new fromStore.LoadDisconnectReasons());
  }

  ngAfterContentInit(): void {
    if (this.dialogData.originPage) {
      this.ticketFormGroup.controls.supportType.setValue(
        this.dialogData.originPage,
      );
    }
    this.selectedServiceId = this.dialogData?.selectedServiceId || null;
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  makeTicketSelection(): boolean {
    this.formSubmitAttempted = true;
    this.cdr.markForCheck();
    this.validateFields();
    if (this.ticketFormGroup.value.supportType !== '') {
      this.isTicketOptionSelectedSubject.next(true);
      this.dialogRef.updateSize(this.dialogWidth, 'calc(100vh - 20px)');
    }
    return false;
  }

  validateFields(): void {
    for (const prop in this.ticketFormGroup.controls) {
      // eslint-disable-next-line no-prototype-builtins
      if (this.ticketFormGroup.controls.hasOwnProperty(prop)) {
        const control: any = this.ticketFormGroup.controls[prop];
        this.inlineErrors[prop] = control.valid
          ? null
          : 'This is a required field';
        this.cdr.markForCheck();
      }
    }
  }

  submitTicket(): void {
    this.ticketSubmittedSubject.next(true);
    this.dialogRef.updateSize(this.dialogWidth, '550px');
  }

  submitNocForm(request): void {
    this.isSubmittingSubject.next(true);

    this.ticketService
      .createNocTicket(request)
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          this.hasSubmissionErrorSubject.next(false);
          setTimeout(() => {
            this.store.dispatch(new fromStore.ClearTickets());
            this.store.dispatch(new fromStore.LoadTickets());
          }, 1000);
          this.submitTicket();
          this.cdr.markForCheck();
        }),
        catchError((error) => {
          this.hasSubmissionErrorSubject.next(true);
          this.getErrorMessage(error);
          this.cdr.markForCheck();
          return EMPTY; // Prevents the stream from breaking
        }),
        finalize(() => this.isSubmittingSubject.next(false)),
      )
      .subscribe();
  }

  submitCareForm(request): void {
    this.isSubmittingSubject.next(true);

    this.ticketService
      .createCareTicket(request)
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          this.hasSubmissionErrorSubject.next(false);
          setTimeout(() => {
            this.store.dispatch(new fromStore.ClearTickets());
            this.store.dispatch(new fromStore.LoadTickets());
          }, 1000);
          this.submitTicket();
          this.cdr.markForCheck();
        }),
        catchError((error) => {
          this.hasSubmissionErrorSubject.next(true);
          this.getErrorMessage(error);
          this.cdr.markForCheck();
          return EMPTY; // Prevents stream from breaking
        }),
        finalize(() => this.isSubmittingSubject.next(false)),
      )
      .subscribe();
  }

  submitBillForm(request): void {
    this.isSubmittingSubject.next(true);

    this.ticketService
      .createBillingTicket(request)
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          this.hasSubmissionErrorSubject.next(false);
          setTimeout(() => {
            this.store.dispatch(new fromStore.ClearTickets());
            this.store.dispatch(new fromStore.LoadTickets());
          }, 1000);
          this.submitTicket();
          this.cdr.markForCheck();
        }),
        catchError((error) => {
          this.hasSubmissionErrorSubject.next(true);
          this.getErrorMessage(error);
          this.cdr.markForCheck();
          return EMPTY; // Prevents stream from breaking
        }),
        finalize(() => this.isSubmittingSubject.next(false)),
      )
      .subscribe();
  }

  submitOrderForm(request): void {
    this.isSubmittingSubject.next(true);

    this.orderService
      .createOrder(request)
      .pipe(
        takeUntil(this.destroy$),
        tap((dialogData) => {
          this.hasSubmissionErrorSubject.next(false);
          this.createdOrder = dialogData;
          this.store.dispatch(new fromStore.ClearOrders());
          this.store.dispatch(new fromStore.LoadOrders());
          this.submitTicket();
          this.cdr.markForCheck();
        }),
        catchError((error) => {
          this.hasSubmissionErrorSubject.next(true);
          this.getErrorMessage(error);
          this.cdr.markForCheck();
          return EMPTY; // Prevents stream from breaking
        }),
        finalize(() => this.isSubmittingSubject.next(false)),
      )
      .subscribe();
  }

  closeDialog(): void {
    this.isSubmittingSubject.next(false);
    this.hasSubmissionErrorSubject.next(false);
    this.errorMessage = null;
    this.cdr.markForCheck();
    this.dialogRef.close();
  }

  goBack(): void {
    this.isTicketOptionSelectedSubject.next(false);
    this.isSubmittingSubject.next(false);
    this.hasSubmissionErrorSubject.next(false);
    this.errorMessage = null;
    this.cdr.markForCheck();
    this.dialogRef.updateSize(this.dialogWidth, this.dialogHeight);
  }

  onSupportTypeChange(event: MatSelectChange): void {
    this.validateFields();

    const { value } = event;

    if (value === this.supportTypeLabel.SECURITY) {
      const url = environment.iscUrl;

      // Open in a new tab
      window.open(url, '_blank', 'noopener,noreferrer');

      // Reset selection so user doesn't proceed into your internal flow
      this.ticketFormGroup.patchValue(
        { supportType: '' },
        { emitEvent: false },
      );
      this.inlineErrors.supportType = null;
      this.cdr.markForCheck();
      this.closeDialog();
    }
  }

  private getErrorMessage(error: any): void {
    if (error.errors && Array.isArray(error.errors)) {
      this.errorMessage = error.errors.join('\n');
    } else {
      this.errorMessage =
        error.message ||
        'Server Error: Unable to submit the ticket. Please try again!';
    }
    this.cdr.markForCheck();
  }
}
